import {useSuspenseQueries} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';
import {parseDateWithFormat} from '@shared/dateUtils';
import {
  type AchievementCriteria,
  type ApiResponseGetCciUbiResponse,
  type ApiResponseListGetInsuranceDiscountsResponse,
  Discount,
  type ReviewInfo,
} from '@di-shared/api/di/__generated';
import {DiscountType} from '@di-shared/api/di/type';
import {ERROR, UNIT} from '@di-shared/const';
import {roundToFixed} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';

export enum UbiCase {
  CASE_1 = 1,
  CASE_11 = 11,
}

interface DisplayInsuranceItem {
  insuranceCompany: string;
  serviceId: string;
  connectServiceDiscount: number;
  safeDrivingScoreDiscount: number;
  totalDiscount: number;
}

interface DisplayInsuranceDetailItem {
  serviceId: string;
  insuranceCompany: string;
  discounts: Array<Discount & {displayDiscountPercentage: string}>;
  reviewInfo: ReviewInfo;
  website: string;
  achievementCriteria: Record<string, AchievementCriteria>;
}

function useUbi({vin, serviceId = ''}: {vin: string; serviceId?: string}) {
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const [{data: ubiData}, {data: insuranceData}] = useSuspenseQueries({
    queries: [
      {
        queryKey: [getQueryKey(diApi.ubiController.getUbi4), vin],
        queryFn: () => diApi.ubiController.getUbi4({vin}),
        select: ({data}: ApiResponseGetCciUbiResponse) => {
          return {
            ...data,
            scoresDate: parseDateWithFormat(data.scoreDate),
            agreeDate: parseDateWithFormat(data.agreeDate),
          };
        },
      },
      {
        queryKey: [getQueryKey(diApi.specialInsuranceContractController.getInsuranceDiscounts)],
        queryFn: () => diApi.specialInsuranceContractController.getInsuranceDiscounts(),
        select: (data: ApiResponseListGetInsuranceDiscountsResponse) => data.data,
      },
    ],
  });

  const {
    agreeDate,
    rangeDrvDist,
    safetyDrvScore,
    safetyDrvScore100,
    safetyDrvScore300,
    safetyDrvScore500,
    safetyDrvScore700,
    safetyDrvScore1000,
  } = ubiData;

  const predictionScoresByDistance = [safetyDrvScore100, safetyDrvScore300, safetyDrvScore500, safetyDrvScore700, safetyDrvScore1000];
  const predictionMinScoreByDistance = Math.min(...predictionScoresByDistance);
  const predictionMaxScoreByDistance = Math.max(...predictionScoresByDistance);

  // 보험사 목록 UI용 data
  const displayInsurancesData: Array<DisplayInsuranceItem> = [];
  insuranceData.forEach((insuranceItem) => {
    let connectServiceDiscount = 0;
    let safeDrivingScoreDiscount = 0;
    insuranceItem.discounts.forEach((discountItem) => {
      if (discountItem.agreeType === DiscountType.SAFE_DRIVING_SCORE) {
        // 보험사의 최소 조건(안전운전 점수 + 주행 거리)를 넘겨야함
        if (safetyDrvScore >= discountItem.drivingScore && rangeDrvDist >= discountItem.drivingDistance) {
          safeDrivingScoreDiscount = Math.max(safeDrivingScoreDiscount, discountItem.maxDiscountPercentage);
        }
      } else if (discountItem.agreeType === DiscountType.CONNECTED_SERVICES) {
        connectServiceDiscount = Math.max(connectServiceDiscount, discountItem.maxDiscountPercentage);
      }
    });

    displayInsurancesData.push({
      insuranceCompany: insuranceItem.insuranceCompany,
      serviceId: insuranceItem.serviceId,
      connectServiceDiscount,
      safeDrivingScoreDiscount,
      totalDiscount: plusAndMultiplePercentage([connectServiceDiscount, safeDrivingScoreDiscount]),
    });
  });
  displayInsurancesData.sort((a, b) => b.totalDiscount - a.totalDiscount);

  // 보험 상세 페이지 UI용 data 가공
  let displayInsuranceDetailData: DisplayInsuranceDetailItem | null = null;
  const currentInsuranceItem = insuranceData.find((insuranceItem) => insuranceItem.serviceId === serviceId);
  if (currentInsuranceItem) {
    const displayDiscounts: Array<Discount & {displayDiscountPercentage: string}> = [];
    currentInsuranceItem.discounts.forEach((discountItem) => {
      const existing = displayDiscounts.find((item) => item.agreeType === discountItem.agreeType);
      if (!existing) {
        displayDiscounts.push({
          ...discountItem,
          displayDiscountPercentage:
            discountItem.minDiscountPercentage === discountItem.maxDiscountPercentage
              ? `${discountItem.maxDiscountPercentage}${UNIT.PERCENT}`
              : `${discountItem.minDiscountPercentage}~${discountItem.maxDiscountPercentage}${UNIT.PERCENT}`,
        });
      } else {
        const minValue = Math.min(existing.minDiscountPercentage, discountItem.minDiscountPercentage);
        const maxValue = Math.max(existing.maxDiscountPercentage, discountItem.maxDiscountPercentage);
        existing.displayDiscountPercentage += `, ${discountItem.maxDiscountPercentage}${UNIT.PERCENT}`;
        existing.minDiscountPercentage = minValue;
        existing.maxDiscountPercentage = maxValue;
      }
    });
    displayInsuranceDetailData = {
      ...currentInsuranceItem,
      discounts: [...displayDiscounts],
      achievementCriteria: currentInsuranceItem.achievementCriteria,
    };
  }

  // 퍼센트 계산 (n)
  function getPredictionPercentage(minSafeDrivingScore: number, maxSafeDrivingScore = Infinity, rangeDrvDist = 0) {
    let n = 0;
    insuranceData.forEach((insuranceItem) => {
      let ccsSignupPercentage = 0;
      const ubiDiscountPercentages: Array<number> = [0];
      insuranceItem.discounts.forEach((discountItem) => {
        if (discountItem.agreeType === DiscountType.CONNECTED_SERVICES) {
          ccsSignupPercentage = discountItem.maxDiscountPercentage;
        } else if (discountItem.agreeType === DiscountType.SAFE_DRIVING_SCORE) {
          if (
            (maxSafeDrivingScore === Infinity && minSafeDrivingScore >= discountItem.drivingScore) ||
            (maxSafeDrivingScore !== Infinity &&
              minSafeDrivingScore <= discountItem.drivingScore &&
              discountItem.drivingScore < maxSafeDrivingScore)
          ) {
            // 거리 기반 ccs 특약 조건 추가
            if (rangeDrvDist > 0) {
              if (rangeDrvDist >= discountItem.drivingDistance) {
                ubiDiscountPercentages.push(discountItem.maxDiscountPercentage);
              }
            } else {
              ubiDiscountPercentages.push(discountItem.maxDiscountPercentage);
            }
          }
        }
      });

      n = Math.max(n, plusAndMultiplePercentage([ccsSignupPercentage, Math.max(...ubiDiscountPercentages)]));
    });
    return n;
  }

  function getDiscountPredictionCase() {
    // 1. ERROR CASE 영역 비노출 (no data, 데이터 수신 불가, etc)
    if (rangeDrvDist <= 0) {
      return {
        ubiCase: 1,
        agreeDate: agreeDate ? CldrDatetimeFormatter.format(agreeDate, 'yMMMd') : ERROR.DASH,
      };
    }
    // 2. 주행거리 부족 CASE1
    // 90일간 주행거리 500km 미만
    else if (rangeDrvDist < 500) {
      return {
        ubiCase: 2,
        // 500km - 누적 주행거리
        N: 500 - rangeDrvDist,
      };
    }
    // 3. 주행거리 부족 CASE2
    // 90일간 주행거리 500km 이상 ~ 1000km 미만 && 현재 안전운전 점수 90점 이상 && 예측점수 중 제일 낮은 점수가 90점 이상인 경우
    else if (500 <= rangeDrvDist && rangeDrvDist < 1000 && safetyDrvScore >= 90 && predictionMinScoreByDistance >= 90) {
      return {
        ubiCase: 3,
        // 1000km - 누적 주행거리
        N: 1000 - rangeDrvDist,
      };
    }
    // 4. 최대 할인율 달성
    // 90일간 주행거리 1000km 이상 && 현재 안전운전 점수 90점 이상 && 예측점수 중 제일 낮은 점수가 90점 이상인 경우
    else if (rangeDrvDist >= 1000 && safetyDrvScore >= 90 && predictionMinScoreByDistance >= 90) {
      return {
        ubiCase: 4,
        // ccs 가입특약 + ubi 특약 곱산시 합산시 최대할인율
        n: getPredictionPercentage(safetyDrvScore),
      };
    }
    // 5. 예측점수 기반 할인율 상승 CASE1
    // 90일간 주행거리 500km 이상 && 현재 안전운전 점수 70점 미만 && 예측점수 중 제일 높은 점수가 70점 이상인 경우 (a% -> b%)
    else if (rangeDrvDist >= 500 && safetyDrvScore < 70 && predictionMaxScoreByDistance >= 70) {
      return {
        ubiCase: 5,
        // 현재 점수 && 거리 기반 ccs 특약 + ubi 특약 곱산시 최대할인율
        a: getPredictionPercentage(safetyDrvScore, Infinity, rangeDrvDist),
        // 70점 이상~90점 미만인 경우 ccs 특약 + ubi 특약 곱산시 최대할인율
        b: getPredictionPercentage(70, 90),
        // 예측점수 중 제일 높은 점수를 달성하기 위해 추가로 주행해야할 거리
        d: [100, 300, 500, 700, 1000][predictionScoresByDistance.indexOf(predictionMaxScoreByDistance)],
        // 예측점수 중 제일 높은 점수
        f: predictionMaxScoreByDistance,
      };
    }
    // 6. 예측점수 기반 할인율 상승 CASE2
    // 90일간 주행거리 500km 이상 && 현재 안전운전 점수 70점 이상~90점 미만 && 예측점수 중 제일 낮은 점수가 90점 이상인 경우
    else if (rangeDrvDist >= 500 && 70 <= safetyDrvScore && safetyDrvScore < 90 && predictionMinScoreByDistance >= 90) {
      return {
        ubiCase: 6,
        // 90점 이상시 ccs 특약 + ubi 특약 곱산시 최대할인율
        c: getPredictionPercentage(90),
        // 예측점수 중 제일 낮은 점수를 달성하기 위해 추가로 주행해야할 거리
        e: [100, 300, 500, 700, 1000][predictionScoresByDistance.indexOf(predictionMinScoreByDistance)],
        // 예측점수 중 제일 낮은 점수
        g: predictionMinScoreByDistance,
      };
    }
    // 7. 안전운전 점수 상태 안내 CASE1
    // 90일간 주행거리 500km 이상 && 현재 안전운전 점수 70점 이상~90점 미만 && 예측점수 중 제일 낮은 점수가 70점 이상~90점 미만인 경우
    else if (
      rangeDrvDist >= 500 &&
      70 <= safetyDrvScore &&
      safetyDrvScore < 90 &&
      70 <= predictionMinScoreByDistance &&
      predictionMinScoreByDistance < 90
    ) {
      return {
        ubiCase: 7,
        // 90점 - 현재 안전운전 점수
        a: 90 - safetyDrvScore,
        // 90점 이상시 ccs 특약 + ubi 특약 곱산시 최대할인율
        b: getPredictionPercentage(90),
        // 현재 점수 && 거리 기반 ccs 특약 + ubi 특약 곱산시 최대할인율
        c: getPredictionPercentage(safetyDrvScore, Infinity, rangeDrvDist),
      };
    }
    // 8. 안전운전 점수 상태 안내 CASE2
    // 90일간 주행거리 500km 이상 && 현재 안전운전 점수 70점 미만 && 예측점수 중 제일 높은 점수가 70점 미만인 경우
    else if (rangeDrvDist >= 500 && safetyDrvScore < 70 && predictionMaxScoreByDistance < 70) {
      return {
        ubiCase: 8,
        // 70점 - 현재 안전운전 점수
        d: 70 - safetyDrvScore,
        // 70~90점 미만인 경우 ccs 특약 + ubi 특약 곱산시 최대할인율
        e: getPredictionPercentage(70, 90),
      };
    }
    // 9. 할인율 하락 안내 CASE1
    // 90일간 주행거리 1000km 이상 && 현재 안전운전 점수 90점 이상 && 예측점수 중 제일 낮은 점수가 90점 미만인 경우
    else if (rangeDrvDist >= 1000 && safetyDrvScore >= 90 && predictionMinScoreByDistance < 90) {
      return {
        ubiCase: 9,
        // 현재 점수 && 거리 기반 ccs 특약 + ubi 특약 곱산시 최대할인율
        a: getPredictionPercentage(safetyDrvScore, Infinity, rangeDrvDist),
        // 70~90점 미만인 경우 ccs 특약 + ubi 특약 곱산시 최대할인율
        b: getPredictionPercentage(70, 90),
        // 예측점수 중 제일 낮은 점수를 달성하기 위해 추가로 주행해야할 거리
        c: [100, 300, 500, 700, 1000][predictionScoresByDistance.indexOf(predictionMinScoreByDistance)],
        // 예측점수 중 제일 낮은 점수
        d: predictionMinScoreByDistance,
      };
    }
    // 10. 할인율 하락 안내 CASE2,3
    // 90일간 주행거리 500km 이상 && 현재 안전운전 점수 70점이상~90점 미만 && 예측점수 중 제일 낮은 점수가 70점 미만인 경우
    // 90일간 주행거리 500km 이상~1000km 미만 && 현재 안전운전 점수 90점 이상 && 예측점수 중 제일 낮은 점수가 90점 미만인 경우
    else if (
      (rangeDrvDist >= 500 && 70 <= safetyDrvScore && safetyDrvScore < 90 && predictionMinScoreByDistance < 70) ||
      (500 <= rangeDrvDist && rangeDrvDist < 1000 && safetyDrvScore >= 90 && predictionMinScoreByDistance < 90)
    ) {
      return {
        ubiCase: 10,
        // 예측점수 중 제일 낮은 점수를 달성하기 위해 추가로 주행해야할 거리
        c: [100, 300, 500, 700, 1000][predictionScoresByDistance.indexOf(predictionMinScoreByDistance)],
        // 예측점수 중 제일 낮은 점수
        d: predictionMinScoreByDistance,
      };
    }
    // 11. 데이터 수신 불가 + etc 에러
    else {
      return {
        ubiCase: 11,
      };
    }
  }

  return {
    getDiscountPredictionCase,
    ubiData,
    insuranceData,
    displayInsurancesData,
    displayInsuranceDetailData,
  };
}

// 곱산식
function plusAndMultiplePercentage(percentages: Array<number>) {
  const totalPercentage = 1 - percentages.reduce((acc, percentage) => acc * (1 - percentage * 0.01), 1);
  return Number(roundToFixed(Number(totalPercentage * 100), 1));
}

export default useUbi;
